import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Search, Pencil, Trash2, SlidersHorizontal, X, Star } from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { SiteForm } from '../../components/forms/SiteForm';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { siteApi, companyApi, wellApi } from '../../services/api-services';
import type { Site, Company } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// Locations Management — Operations Deck (Variation A)
// Form lives in components/forms/SiteForm.tsx
//
// Columns: Company | Surface Location | Country | State/Province | Wells | Modified | Actions
// SuperAdmin sees Company column; CompanyAdmin (and others) get it hidden.
// ─────────────────────────────────────────────────────────────

// Columns: [Company]? · Surface Location · Country · State · Wells · Actions
const COL_TEMPLATE_ADMIN =
  'minmax(200px,1.6fr) minmax(240px,2fr) minmax(120px,1fr) minmax(140px,1.1fr) 90px 120px';
const COL_TEMPLATE_USER =
  'minmax(240px,2fr) minmax(120px,1fr) minmax(140px,1.1fr) 90px 120px';

// Sentinel sortCol used for client-side Wells column sorting.
const SORT_WELLS = '__wells__';

// State / Province → 2-letter ISO abbreviation lookup
const STATE_ABBREV: Record<string, string> = {
  Alabama:'AL', Alaska:'AK', Arizona:'AZ', Arkansas:'AR', California:'CA',
  Colorado:'CO', Connecticut:'CT', Delaware:'DE', Florida:'FL', Georgia:'GA',
  Hawaii:'HI', Idaho:'ID', Illinois:'IL', Indiana:'IN', Iowa:'IA',
  Kansas:'KS', Kentucky:'KY', Louisiana:'LA', Maine:'ME', Maryland:'MD',
  Massachusetts:'MA', Michigan:'MI', Minnesota:'MN', Mississippi:'MS', Missouri:'MO',
  Montana:'MT', Nebraska:'NE', Nevada:'NV', 'New Hampshire':'NH', 'New Jersey':'NJ',
  'New Mexico':'NM', 'New York':'NY', 'North Carolina':'NC', 'North Dakota':'ND',
  Ohio:'OH', Oklahoma:'OK', Oregon:'OR', Pennsylvania:'PA', 'Rhode Island':'RI',
  'South Carolina':'SC', 'South Dakota':'SD', Tennessee:'TN', Texas:'TX', Utah:'UT',
  Vermont:'VT', Virginia:'VA', Washington:'WA', 'West Virginia':'WV',
  Wisconsin:'WI', Wyoming:'WY', 'District of Columbia':'DC',
  Alberta:'AB', 'British Columbia':'BC', Manitoba:'MB', 'New Brunswick':'NB',
  'Newfoundland and Labrador':'NL', 'Northwest Territories':'NT', 'Nova Scotia':'NS',
  Nunavut:'NU', Ontario:'ON', 'Prince Edward Island':'PE', Quebec:'QC',
  Saskatchewan:'SK', Yukon:'YT',
};

function abbrevState(s: string): string {
  if (!s) return '';
  const trimmed = s.trim();
  if (trimmed.length <= 3) return trimmed.toUpperCase();
  return STATE_ABBREV[trimmed] ?? trimmed;
}

const COUNTRY_SHORT: Record<string, string> = {
  unitedstatesofamerica: 'USA',
  unitedstates: 'USA',
  usa: 'USA',
  us: 'USA',
  america: 'USA',
  unitedkingdom: 'UK',
  uk: 'UK',
  greatbritain: 'UK',
  england: 'UK',
};
function shortCountry(c: string): string {
  if (!c) return '';
  const key = c.toLowerCase().replace(/[^a-z0-9]/g, '');
  return COUNTRY_SHORT[key] ?? c.trim();
}

export function SitesPage() {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const isCompanyAdmin = authUser?.role === 'CompanyAdmin';
  const canManageSites = isSuperAdmin || isCompanyAdmin;

  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const crud = useCrudPage<Site>({ fetchFn: siteApi.getAll });
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);

  // Map siteId → well count, hydrated as pages load.
  const [wellCounts, setWellCounts] = useState<Record<number, number>>({});

  const recordActivity = useActivityStore((s) => s.record);
  const username = useAuthStore((s) => s.user?.username);

  // Load company list once — used by the form picker & filter dropdown.
  useEffect(() => {
    companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
  }, []);

  // Fetch well counts for the current page of sites.
  // Backend's getAllBySiteId returns totalCount — we ignore the entities to keep payload small.
  useEffect(() => {
    if (!crud.data.length) return;
    let cancelled = false;
    const missing = crud.data.filter((s) => s.id != null && wellCounts[s.id] === undefined);
    if (missing.length === 0) return;
    Promise.all(
      missing.map((s) =>
        wellApi
          .getAllBySiteId(s.id, { limit: 1 })
          .then((r) => ({ id: s.id, n: r.numberofEntities ?? r.entities?.length ?? 0 }))
          .catch(() => ({ id: s.id, n: -1 })),
      ),
    ).then((results) => {
      if (cancelled) return;
      setWellCounts((prev) => {
        const next = { ...prev };
        for (const { id, n } of results) next[id] = n;
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [crud.data, wellCounts]);

  const handleSave = async (form: Partial<Site>) => {
    setSaving(true);
    try {
      const label = form.siteName?.trim() || 'Untitled location';
      if (crud.editingItem) {
        await siteApi.update(form);
        crud.showToast('Location updated successfully');
        recordActivity({ type: 'update', entity: 'Locations', label, user: username });
      } else {
        await siteApi.create(form);
        crud.showToast('Location created successfully');
        recordActivity({ type: 'create', entity: 'Locations', label, user: username });
      }
      crud.closeModal();
      crud.load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save location';
      crud.showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (site: Site) => {
    // Guard: prevent delete if wells still attached.
    try {
      const wellsRes = await wellApi.getAllBySiteId(site.id, { limit: 1 });
      const count = wellsRes.numberofEntities ?? wellsRes.entities?.length ?? 0;
      if (count > 0) {
        crud.showToast(
          `This location has ${count} well${count === 1 ? '' : 's'}. Delete or reassign wells first.`,
          'error',
        );
        return;
      }
    } catch { /* fall through */ }
    if (!confirm(`Delete location "${site.siteName}"?`)) return;
    try {
      await siteApi.delete({ id: site.id });
      crud.showToast('Location deleted');
      recordActivity({
        type: 'delete',
        entity: 'Locations',
        label: site.siteName?.trim() || `Location #${site.id}`,
        user: username,
      });
      crud.load();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete location';
      crud.showToast(message, 'error');
    }
  };

  // Client-side filter pass on the current page.
  const filteredData = useMemo(() => {
    const filtered = crud.data.filter((s) => {
      if (companyFilter && s.companyId !== companyFilter) return false;
      if (countryFilter && (s.country ?? '').trim() !== countryFilter) return false;
      if (stateFilter && (s.stateProvince ?? '').trim() !== stateFilter) return false;
      return true;
    });
    // Wells column sorts client-side because the count comes from a per-row fetch.
    // Backend sort uses companyName / SiteName / Country / StateProvince directly.
    if (crud.sortCol === SORT_WELLS) {
      const dir = crud.sortDir === 'Ascending' ? 1 : -1;
      return [...filtered].sort((a, b) => {
        const ca = a.id != null ? wellCounts[a.id] : undefined;
        const cb = b.id != null ? wellCounts[b.id] : undefined;
        // Treat undefined (still loading) as -1 sentinel so it sinks to the bottom on ascending.
        const va = ca == null || ca < 0 ? -1 : ca;
        const vb = cb == null || cb < 0 ? -1 : cb;
        return (va - vb) * dir;
      });
    }
    return filtered;
  }, [crud.data, crud.sortCol, crud.sortDir, companyFilter, countryFilter, stateFilter, wellCounts]);

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Distinct dropdown options derived from loaded page.
  const availableCountries = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((x) => x.country?.trim() && s.add(x.country.trim()));
    return Array.from(s).sort();
  }, [crud.data]);
  const availableStates = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((x) => {
      if (countryFilter && (x.country ?? '').trim() !== countryFilter) return;
      if (x.stateProvince?.trim()) s.add(x.stateProvince.trim());
    });
    return Array.from(s).sort();
  }, [crud.data, countryFilter]);

  const filtersActive = !!companyFilter || !!countryFilter || !!stateFilter;
  const clearFilters = () => {
    setCompanyFilter('');
    setCountryFilter('');
    setStateFilter('');
  };

  // Stats — domain summary across the loaded page.
  const stats = useMemo(() => {
    const totalWells = Object.values(wellCounts).reduce((acc, n) => acc + (n > 0 ? n : 0), 0);
    const companyIds = new Set<number>();
    crud.data.forEach((s) => s.companyId && companyIds.add(s.companyId));
    const countries = new Set<string>();
    crud.data.forEach((s) => s.country?.trim() && countries.add(s.country.trim()));
    return {
      total: crud.totalCount,
      wells: totalWells,
      companies: companyIds.size,
      countries: countries.size,
    };
  }, [crud.data, crud.totalCount, wellCounts]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  // TopBar primary action.
  usePageAction(
    canManageSites ? (
      <PrimaryButton onClick={crud.openCreate}>
        <Plus size={14} /> Add location
      </PrimaryButton>
    ) : null,
    [canManageSites],
  );

  const colTemplate = isSuperAdmin ? COL_TEMPLATE_ADMIN : COL_TEMPLATE_USER;
  const activeFilterCount = (companyFilter ? 1 : 0) + (countryFilter ? 1 : 0) + (stateFilter ? 1 : 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <StatsCard label="Locations" value={stats.total} sub="Surface pads in portal" accent="var(--color-accent)" loading={crud.loading} />
        <StatsCard label="Wells (this page)" value={stats.wells} sub="Across loaded locations" accent="var(--color-teal)" loading={crud.loading} />
        <StatsCard label="Companies" value={stats.companies} sub="With locations on this page" accent="#7c6ba8" loading={crud.loading} />
        <StatsCard label="Countries" value={stats.countries} sub="Geographic spread" accent="var(--color-warn)" loading={crud.loading} />
      </div>

      {/* Filter / search bar + grid */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 280,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search locations…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          <FilterPopover
            isSuperAdmin={isSuperAdmin}
            companies={companies}
            companyFilter={companyFilter}
            countryFilter={countryFilter}
            stateFilter={stateFilter}
            availableCountries={availableCountries}
            availableStates={availableStates}
            onChangeCompany={setCompanyFilter}
            onChangeCountry={(v) => {
              setCountryFilter(v);
              if (v && stateFilter && availableStates.indexOf(stateFilter) === -1) setStateFilter('');
            }}
            onChangeState={setStateFilter}
            onClear={clearFilters}
            activeCount={activeFilterCount}
          />

          {filtersActive && (
            <button
              onClick={clearFilters}
              style={{
                fontSize: 12,
                color: 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: 5,
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(11,74,111,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : filtersActive
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} location${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: colTemplate,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          {isSuperAdmin && (
            <SortHeader
              label="Company"
              col="companyName"
              activeCol={crud.sortCol}
              dir={crud.sortDir}
              onClick={handleSort}
            />
          )}
          <SortHeader
            label="Surface Location"
            col="SiteName"
            activeCol={crud.sortCol}
            dir={crud.sortDir}
            onClick={handleSort}
          />
          <SortHeader
            label="Country"
            col="Country"
            activeCol={crud.sortCol}
            dir={crud.sortDir}
            onClick={handleSort}
          />
          <SortHeader
            label="State / Province"
            col="StateProvince"
            activeCol={crud.sortCol}
            dir={crud.sortDir}
            onClick={handleSort}
          />
          <SortHeader
            label="Wells"
            col={SORT_WELLS}
            activeCol={crud.sortCol}
            dir={crud.sortDir}
            onClick={handleSort}
            align="right"
          />
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {crud.loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
            Loading…
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <MapPin size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {filtersActive ? 'No locations match these filters' : 'No locations found'}
            </div>
            <div style={{ fontSize: 12 }}>
              {filtersActive
                ? 'Try clearing filters or moving to another page.'
                : canManageSites
                  ? 'Add a new location to get started.'
                  : 'Locations registered by your company will appear here.'}
            </div>
          </div>
        ) : (
          <div>
            {filteredData.map((s, i) => (
              <SiteRow
                key={s.id ?? i}
                site={s}
                wellCount={s.id != null ? wellCounts[s.id] : undefined}
                showCompany={isSuperAdmin}
                colTemplate={colTemplate}
                canManage={canManageSites}
                isLast={i === filteredData.length - 1}
                onEdit={() => crud.openEdit(s)}
                onDelete={() => handleDelete(s)}
                onViewWells={() =>
                  navigate(
                    `/dashboard/wells?siteId=${s.id}&siteName=${encodeURIComponent(s.siteName ?? '')}`,
                  )
                }
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>
            {filtersActive && filteredData.length !== crud.data.length && (
              <> (of {crud.data.length} on page)</>
            )}{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> locations
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
              <PaginationButton onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))} disabled={crud.offset === 0}>
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton onClick={() => crud.setOffset(crud.offset + crud.pageSize)} disabled={currentPage >= totalPages}>
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Operations Deck form */}
      <SiteForm
        open={crud.modalOpen}
        editing={crud.editingItem}
        saving={saving}
        companies={companies}
        lockedCompanyId={
          !isSuperAdmin && authUser?.companyId ? Number(authUser.companyId) : undefined
        }
        onCancel={crud.closeModal}
        onSave={handleSave}
      />

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function SiteRow({
  site: s,
  wellCount,
  showCompany,
  colTemplate,
  canManage,
  isLast,
  onEdit,
  onDelete,
  onViewWells,
}: {
  site: Site;
  wellCount: number | undefined;
  showCompany: boolean;
  colTemplate: string;
  canManage: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onViewWells: () => void;
}) {
  const [hover, setHover] = useState(false);
  const country = shortCountry(s.country ?? '');
  const stateAbbrev = abbrevState(s.stateProvince ?? '');

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 14,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      {showCompany && (
        <Cell ellipsis dim title={s.company?.companyName ?? ''}>
          {s.company?.companyName ?? '—'}
        </Cell>
      )}

      {/* Surface location with default badge */}
      <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--color-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            title={s.siteName ?? ''}
          >
            {s.siteName ?? '—'}
            {s.isDefaultSite && (
              <Star
                size={12}
                style={{
                  color: 'var(--color-warn)',
                  fill: 'var(--color-warn)',
                  flexShrink: 0,
                }}
              />
            )}
          </div>
          {s.siteNumber?.trim() && (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--color-text-dim)',
                marginTop: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              #{s.siteNumber}
            </div>
          )}
        </div>
      </div>

      <Cell mono dim>{country || '—'}</Cell>
      <Cell mono dim>{stateAbbrev || '—'}</Cell>

      {/* Wells count chip (deep-links to wells list) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <WellCountChip count={wellCount} onClick={onViewWells} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        {canManage && (
          <>
            <IconButton title="Edit" onClick={onEdit}>
              <Pencil size={15} />
            </IconButton>
            <IconButton title="Delete" onClick={onDelete} hoverDanger>
              <Trash2 size={15} />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}


function WellCountChip({ count, onClick }: { count: number | undefined; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  if (count === undefined) {
    return (
      <span
        style={{
          display: 'inline-block',
          width: 28,
          height: 18,
          borderRadius: 4,
          background: 'var(--color-panel-alt)',
          opacity: 0.6,
        }}
      />
    );
  }
  if (count < 0) {
    return (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>—</span>
    );
  }
  const isZero = count === 0;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); if (!isZero) onClick(); }}
      onMouseEnter={() => !isZero && setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={isZero}
      title={isZero ? 'No wells yet' : `View ${count} well${count === 1 ? '' : 's'}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 9px',
        borderRadius: 12,
        fontFamily: 'var(--font-mono)',
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: 1,
        border: '1px solid',
        background: isZero
          ? 'var(--color-panel-alt)'
          : hover
            ? 'rgba(11,74,111,0.10)'
            : 'rgba(11,74,111,0.05)',
        color: isZero ? 'var(--color-text-dim)' : 'var(--color-accent)',
        borderColor: isZero
          ? 'var(--color-border)'
          : hover
            ? 'var(--color-accent)'
            : 'rgba(11,74,111,0.25)',
        cursor: isZero ? 'default' : 'pointer',
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      {count}
    </button>
  );
}

function Cell({
  children,
  mono = false,
  dim = false,
  ellipsis = false,
  style,
  title,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  ellipsis?: boolean;
  style?: CSSProperties;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: dim ? 'var(--color-text-mid)' : 'var(--color-text)',
        fontSize: mono ? 13 : 14,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const idleBg     = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor  = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg     = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor  = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';

  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${hover ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: hover ? hoverBg : idleBg,
        color: hover ? hoverColor : idleColor,
        transition: 'background 120ms, color 120ms, border-color 120ms, transform 120ms',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        background: hover ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: '#fff',
        border: 'none',
        padding: '9px 14px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Filter popover — portal anchored under the trigger button.
// ─────────────────────────────────────────────────────────────

function FilterPopover({
  isSuperAdmin,
  companies,
  companyFilter,
  countryFilter,
  stateFilter,
  availableCountries,
  availableStates,
  onChangeCompany,
  onChangeCountry,
  onChangeState,
  onClear,
  activeCount,
}: {
  isSuperAdmin: boolean;
  companies: Company[];
  companyFilter: number | '';
  countryFilter: string;
  stateFilter: string;
  availableCountries: string[];
  availableStates: string[];
  onChangeCompany: (v: number | '') => void;
  onChangeCountry: (v: string) => void;
  onChangeState: (v: string) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const POP_WIDTH = 340;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 8;
    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - POP_WIDTH - 12;
    const left = Math.max(12, Math.min(desiredLeft, maxLeft));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7,
          transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-accent)',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: POP_WIDTH,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Filter by
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-dim)',
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isSuperAdmin && (
              <PopoverField label="Company">
                <FilterSelectNumeric
                  value={companyFilter}
                  onChange={onChangeCompany}
                  placeholder="All companies"
                  options={companies.map((c) => ({ value: c.id, label: c.companyName ?? `Company #${c.id}` }))}
                />
              </PopoverField>
            )}
            <PopoverField label="Country">
              <FilterSelect
                value={countryFilter}
                onChange={onChangeCountry}
                placeholder="All countries"
                options={availableCountries}
              />
            </PopoverField>
            <PopoverField label="State / Province">
              <FilterSelect
                value={stateFilter}
                onChange={onChangeState}
                placeholder={availableStates.length ? 'All states' : '— select country first —'}
                options={availableStates}
                disabled={availableStates.length === 0}
              />
            </PopoverField>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
            }}
          >
            <button
              onClick={onClear}
              disabled={activeCount === 0}
              style={{
                fontSize: 12,
                color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: activeCount === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                padding: '4px 4px',
              }}
            >
              Clear filters
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={selectStyle(hasValue, disabled)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function FilterSelectNumeric({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: number | '';
  onChange: (v: number | '') => void;
  options: { value: number; label: string }[];
  placeholder: string;
}) {
  const hasValue = value !== '';
  return (
    <select
      value={value === '' ? '' : String(value)}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      style={selectStyle(hasValue, false)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function selectStyle(hasValue: boolean, disabled: boolean | undefined): CSSProperties {
  return {
    width: '100%',
    height: 34,
    padding: '0 30px 0 11px',
    fontSize: 13,
    fontFamily: 'var(--font-body)',
    color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
    background:
      `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
    border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
    borderRadius: 7,
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    fontWeight: hasValue ? 500 : 400,
    transition: 'border-color 120ms',
  };
}
